import { useState, useEffect, useRef, useCallback, lazy, Suspense } from 'react';
import { useDarkMode } from '@hooks/useDarkMode';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Mic, RotateCcw, ChevronRight } from 'lucide-react';
import { usePageTitle } from '@hooks/usePageTitle';
import { useAuth } from '@contexts/AuthContext';
import { useStreak } from '@hooks/useStreak';
import { useSpeechRecognition } from '@features/sales-negotiation/hooks/useSpeechRecognition';
import { ALL_CHARACTERS, nextCharacterPath, getScenarioOf, isScenarioLive, SCENARIO_TITLES } from '@features/sales-negotiation/data/index.js';
import { audioUrl, liveReplyUrl, openingLine } from '@features/sales-negotiation/audio.js';
import { stageWaitMs } from '@features/sales-negotiation/silence.js';
import { supabase } from '@services/api';

const FALLBACK_ID = 'henry-davies-retired-hobbyist-gardener';

// How long a spoken live reply may take to start playing before the call carries on with the text alone.
const LIVE_AUDIO_START_MS = 6000;

// Dev-only character stepper (localhost). `import.meta.env.DEV` is replaced by `false` in the production
// build, which removes both this import and the render below, so the panel is not shipped.
const DevCharacterPanel = import.meta.env.DEV
  ? lazy(() => import('@features/sales-negotiation/DevCharacterPanel.jsx'))
  : null;

/* ─── API base ───
   Same variable and fallback as useCopyScoring.js: VITE_APP_API_URL already
   ends in /api (locally '/api' via the Vite proxy; in production the backend
   URL + '/api'), so only the feature segment is added here. This used to be
   the bare relative '/api/sales-negotiation', which only worked through the
   dev proxy; on Vercel it hit the SPA rewrite and got index.html back. */
const API_BASE = `${(import.meta.env.VITE_APP_API_URL || 'http://localhost:3000/api').replace(/\/+$/, '')}/sales-negotiation`;

/* ─── Authenticated API calls ───
   Every Voice Room endpoint spends paid DeepSeek usage, so the backend
   requires a login, the beta allowlist and a per-user rate limit. This
   attaches the Supabase access token (same approach as useCopyScoring) and
   turns any non-2xx into a thrown error carrying a message that is fit to show
   the user. Before, a 401/403/429 body still parsed as JSON, so callers read
   `data.text` / `data.coaching` as undefined and carried on with nothing. */
const apiErrorMessage = (status, body) => {
  if (status === 401) return 'Your session has expired. Sign in again to keep practising.';
  if (status === 403) return 'The Voice Room is limited to the private beta.';
  if (status === 429) return body?.error || 'You have used a lot of voice practice in a short time. Wait a few minutes and try again.';
  return body?.error || `The server returned an error (${status}).`;
};

/* ─── What the server accepts as a transcript ───
   /voice/* takes a characterId plus [{ role: 'rep' | 'prospect', text }] and
   rejects anything over 12 turns, 600 characters a turn or 6000 in total. The
   server looks the character up itself, so no character object is sent. This
   maps the on-screen transcript (isUser / speaker / typing placeholders) into
   that shape: empty lines dropped, each line trimmed to the limit, and only the
   most recent turns kept if a long call would go over, so a legitimate call is
   never rejected for its length. */
const WIRE_MAX_TURNS = 12;
const WIRE_MAX_TURN_CHARS = 600;
const WIRE_MAX_TOTAL_CHARS = 6000;

const toWireTranscript = (entries) => {
  const turns = entries
    .filter((e) => typeof e.text === 'string' && e.text.trim())
    .map((e) => ({
      role: e.isUser ? 'rep' : 'prospect',
      text: e.text.trim().slice(0, WIRE_MAX_TURN_CHARS),
      // a prospect line that came from the scripted fallback, not the AI
      ...(e.scripted ? { scripted: true } : {}),
    }))
    .slice(-WIRE_MAX_TURNS);
  while (turns.length > 1 && turns.reduce((n, t) => n + t.text.length, 0) > WIRE_MAX_TOTAL_CHARS) turns.shift();
  return turns;
};

const voiceApi = async (path, { method = 'GET', body } = {}) => {
  const { data: { session } } = await supabase.auth.getSession();
  const token = session?.access_token;
  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });
  if (!res.ok) {
    const errBody = await res.json().catch(() => null);
    const err = new Error(apiErrorMessage(res.status, errBody));
    err.status = res.status;
    throw err;
  }
  return res.json();
};

/* ─── Scripted fallback turns (used when STT unavailable or API fails) ─── */
const SCRIPTED_TURNS = {
  'decision-maker-hook': {
    user: [
      "Hi, Alex from StreamLine — we cut logistics overhead by 23% for companies your size. Two minutes to see if it's relevant?",
      "Completely understand. When did you last benchmark your costs? Most clients find 15–20% waste before switching.",
      "Fair enough. I'll send a one-page case study. Best email?",
    ],
    prospect: [
      { text: "Two minutes. Fine. What are you selling and why should I care?" },
      { text: "It's been a while. But switching is a headache. What does onboarding look like?" },
      { text: "Send it over. If the numbers check out I'll give you 20 minutes next week." },
    ],
  },
  'happy-with-vendor': {
    user: [
      "Totally respect that loyalty. Quick question — when did you last put your contract out to bid?",
      "I'm not asking you to switch today. Just 20 minutes to show you what the market looks like now.",
      "Fair enough. I'll send one case study — your industry, similar size.",
    ],
    prospect: [
      { text: "We haven't rebid in two years but it's not a priority. What makes you different?" },
      { text: "I'll give you 20 minutes. But don't waste it with a generic pitch." },
      { text: "Send it. If it's relevant I'll get back to you." },
    ],
  },
  'price-anchor': {
    user: [
      "I understand that's your target. At that margin we'd operate below cost. What I can offer is 12% with annual commitment.",
      "Their quote doesn't include implementation. Factor that in and you're at parity by month three.",
      "Final number — 12%, annual terms, onboarding included, plus a quarterly business review.",
    ],
    prospect: [
      { text: "A competitor quoted me 20% yesterday. Why should I pay more for you?" },
      { text: "You've made a decent case. Send me a proposal by Friday." },
      { text: "Alright. Get the paperwork over by end of week. Don't make me chase you." },
    ],
  },
};

/* ─── Default silence stages (fallback if character has no silenceProfile) ─── */
const DEFAULT_STAGES = [
  { type: 'warn',   delay: 8000,  lineKey: 'silence_1' },
  { type: 'warn',   delay: 15000, lineKey: 'silence_2' },
  { type: 'hangup', delay: 22000, lineKey: 'hangup'    },
];

/* ─── Audio player ─── */
const useAudioPlayer = () => {
  const audioRef  = useRef(null);
  const [playing, setPlaying] = useState(false);
  /* Resolves when the clip has ended, failed to load, or been refused by the browser: the caller then carries on
     with the text either way. `startTimeoutMs` (live replies only) also gives up on a file that has not started
     playing by then, so a stalled download cannot hold a call up. Pre-made clips pass nothing and behave as before. */
  const play = useCallback((src, { startTimeoutMs = 0 } = {}) => {
    if (!src) return Promise.resolve();
    return new Promise((resolve) => {
      if (audioRef.current) { audioRef.current.pause(); audioRef.current.currentTime = 0; }
      const a = new Audio(src);
      audioRef.current = a;
      let giveUp = null;
      const finish = () => { if (giveUp) clearTimeout(giveUp); setPlaying(false); resolve(); };
      a.addEventListener('ended',  finish);
      a.addEventListener('error',  finish);
      if (startTimeoutMs > 0) {
        a.addEventListener('playing', () => { if (giveUp) { clearTimeout(giveUp); giveUp = null; } });
        giveUp = setTimeout(() => { a.pause(); finish(); }, startTimeoutMs);
      }
      setPlaying(true);
      a.play().catch(finish);
    });
  }, []);
  const stop = useCallback(() => {
    if (audioRef.current) { audioRef.current.pause(); audioRef.current.currentTime = 0; setPlaying(false); }
  }, []);
  return { play, stop, isPlaying: playing };
};

/* ─── Character portrait ───
   `character.avatar` is now legitimately `null` for the 128 characters
   whose art has not been drawn yet (see scripts/fix-character-avatars.mjs
   — the field used to hold a path that 404'd, which is what the inline
   onError handlers here were silently relying on to show initials).
   A null src never fires onError, so the null case has to be branched
   on explicitly or those characters render as an empty circle. */
const CharacterPortrait = ({ character, size, accent }) => {
  const [failed, setFailed] = useState(false);
  const initials = character.name.split(' ').map((n) => n[0]).join('');
  if (!character.avatar || failed) {
    return (
      <span style={{ fontSize: size * 0.33, fontWeight: 900, color: accent }}>
        {initials}
      </span>
    );
  }
  return (
    <img
      src={character.avatar}
      alt={character.name}
      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
      onError={() => setFailed(true)}
    />
  );
};

/* ─── Waveform ─── */
const Waveform = ({ userSpeaking, prospectSpeaking }) => {
  const canvasRef = useRef(null);
  const frame     = useRef(0);
  const animId    = useRef(null);
  const stateRef  = useRef({ user: false, prospect: false });
  useEffect(() => { stateRef.current = { user: userSpeaking, prospect: prospectSpeaking }; }, [userSpeaking, prospectSpeaking]);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const resize = () => {
      const dpr = window.devicePixelRatio || 1;
      const r   = canvas.getBoundingClientRect();
      canvas.width = r.width * dpr; canvas.height = r.height * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize(); window.addEventListener('resize', resize);
    const draw = () => {
      const { user, prospect } = stateRef.current;
      const w = canvas.getBoundingClientRect().width;
      const h = canvas.getBoundingClientRect().height;
      ctx.clearRect(0, 0, w, h);
      const bars = 54, barW = 2.5, gap = 3.5;
      const startX = (w - (bars * (barW + gap) - gap)) / 2;
      const cy = h / 2, maxH = h * 0.88;
      const active = user || prospect;
      const rgb = user ? [34, 197, 94] : prospect ? [14, 165, 233] : [110, 110, 120];
      const f = frame.current;
      for (let i = 0; i < bars; i++) {
        const dist = Math.abs((i / (bars - 1)) - 0.5) * 2;
        const env  = 1 - Math.pow(dist, 1.6);
        const alph = (1 - Math.pow(dist, 1.1) * 0.72) * (active ? 0.88 : 0.18);
        let bh = 3;
        if (active) {
          const s1 = Math.sin(f * 0.07 + i * 0.45) * 0.5 + 0.5;
          const s2 = Math.sin(f * 0.11 + i * 0.70 + 1.8) * 0.5 + 0.5;
          const s3 = Math.sin(f * 0.05 + i * 0.28 + 3.1) * 0.5 + 0.5;
          bh = Math.max(3, (s1 * 0.5 + s2 * 0.3 + s3 * 0.2) * maxH * env * 0.92);
        }
        ctx.fillStyle = `rgba(${rgb[0]},${rgb[1]},${rgb[2]},${alph})`;
        ctx.beginPath();
        ctx.roundRect(startX + i * (barW + gap), cy - bh / 2, barW, bh, 99);
        ctx.fill();
      }
      frame.current++;
      animId.current = requestAnimationFrame(draw);
    };
    animId.current = requestAnimationFrame(draw);
    return () => { cancelAnimationFrame(animId.current); window.removeEventListener('resize', resize); };
  }, []);
  return <canvas ref={canvasRef} style={{ width: '100%', height: 56, display: 'block' }} />;
};

/* ─── Chat Bubble ─── */
const Bubble = ({ speaker, text, isUser, accent, isNew, isDark }) => (
  <div style={{
    display: 'flex', justifyContent: isUser ? 'flex-end' : 'flex-start',
    marginBottom: 12, padding: '0 16px', width: '100%', boxSizing: 'border-box',
    animation: isNew ? 'bubble-in 0.3s cubic-bezier(0.16,1,0.3,1) both' : 'none',
  }}>
    <div style={{
      maxWidth: '72%', padding: '13px 16px',
      borderRadius: isUser ? '18px 4px 18px 18px' : '4px 18px 18px 18px',
      background: isUser
        ? (isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.04)')
        : (isDark ? 'rgba(14,165,233,0.11)' : 'rgba(14,165,233,0.08)'),
      border: `1px solid ${isUser
        ? (isDark ? 'rgba(255,255,255,0.09)' : 'rgba(0,0,0,0.07)')
        : 'rgba(14,165,233,0.18)'}`,
      backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)',
    }}>
      <p style={{ fontSize: 14, lineHeight: 1.6, margin: 0, color: isDark ? 'rgba(255,255,255,0.9)' : '#1f2937', fontWeight: 400 }}>
        {text}
      </p>
      <p style={{
        fontSize: 9, fontWeight: 700, letterSpacing: '0.09em', textTransform: 'uppercase', margin: '6px 0 0',
        color: isUser ? (isDark ? 'rgba(255,255,255,0.28)' : 'rgba(0,0,0,0.3)') : accent,
      }}>
        {isUser ? 'You' : speaker}
      </p>
    </div>
  </div>
);

/* ─── Glow button style ─── */
const glowBtnStyle = (accent) => ({
  position: 'relative', overflow: 'hidden',
  background: `linear-gradient(135deg,${accent}14 0%,${accent}06 100%)`,
  border: 'none', cursor: 'pointer', transition: 'transform 0.2s ease',
});

/* ─── Coaching Panel ─── */
const CoachingPanel = ({ feedback, score, summary, onRestart, onDifferentScenario, onExit, accent, isDark, hungUp, fallbackUsed, progressSaved }) => {
  const txt         = isDark ? 'rgba(255,255,255,0.9)' : '#111827';
  const muted       = isDark ? 'rgba(255,255,255,0.45)' : 'rgba(0,0,0,0.45)';
  const ghostBorder = isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.10)';
  const ghostColor  = isDark ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.55)';

  return (
    <div style={{ animation: 'fade-up 0.4s ease-out both', maxWidth: 500, margin: '0 auto', width: '100%', padding: '0 20px' }}>
      <div style={{ textAlign: 'center', marginBottom: 22 }}>
        <p style={{ fontSize: 11, fontWeight: 700, color: accent, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 8 }}>
          {hungUp ? 'Call Ended' : 'Session Complete'}
        </p>
        <h3 style={{ fontSize: 24, fontWeight: 800, color: txt, letterSpacing: '-0.02em', margin: 0 }}>
          {hungUp ? 'They Hung Up' : 'AI Coach Feedback'}
        </h3>
        {score != null && (
          <div style={{ marginTop: 12, display: 'inline-flex', alignItems: 'center', gap: 8, padding: '6px 16px', borderRadius: 99, background: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)', border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'}` }}>
            <span style={{ fontSize: 22, fontWeight: 900, color: accent }}>{score}</span>
            <span style={{ fontSize: 12, color: muted }}>/100</span>
          </div>
        )}
        {summary && (
          <p style={{ fontSize: 14, color: muted, marginTop: 10, lineHeight: 1.6 }}>{summary}</p>
        )}
        {hungUp && !summary && (
          <p style={{ fontSize: 14, color: muted, marginTop: 8, lineHeight: 1.6 }}>
            The prospect lost patience. In a real conversation, silence kills deals.
          </p>
        )}
        {fallbackUsed && (
          <p style={{ fontSize: 12, color: muted, marginTop: 8, lineHeight: 1.6 }}>
            Some replies in this call were scripted because the AI was unavailable.
          </p>
        )}
        {progressSaved === false && (
          <p style={{ fontSize: 12, color: muted, marginTop: 8, lineHeight: 1.6 }}>
            This call wasn't saved to your progress.
          </p>
        )}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 9, marginBottom: 24 }}>
        {feedback.map((item, i) => (
          <div key={i} style={{
            padding: '14px 18px', borderRadius: 14,
            background: item.type === 'strength' ? 'rgba(34,197,94,0.07)' : item.type === 'improve' ? 'rgba(251,146,60,0.07)' : (isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)'),
            border: `1px solid ${item.type === 'strength' ? 'rgba(34,197,94,0.15)' : item.type === 'improve' ? 'rgba(251,146,60,0.15)' : (isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)')}`,
            animation: `fade-up 0.3s ease-out ${0.08 + i * 0.06}s both`,
          }}>
            <p style={{
              fontSize: 10, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 5,
              color: item.type === 'strength' ? '#22c55e' : item.type === 'improve' ? '#fb923c' : (isDark ? 'rgba(255,255,255,0.25)' : 'rgba(0,0,0,0.28)'),
            }}>
              {item.type === 'strength' ? '↑ Strength' : item.type === 'improve' ? '→ Improve' : 'Note'}
            </p>
            <p style={{ fontSize: 13, color: isDark ? 'rgba(255,255,255,0.7)' : '#374151', lineHeight: 1.6, margin: 0 }}>{item.text}</p>
          </div>
        ))}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <button
          onClick={onRestart}
          style={{
            ...glowBtnStyle(accent),
            width: '100%', padding: '16px 0', borderRadius: 14,
            color: accent, fontWeight: 800, fontSize: 15,
            backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
          }}
          onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-1px)'}
          onMouseLeave={e => e.currentTarget.style.transform = 'translateY(0)'}
        >
          <RotateCcw size={16} /> Try again with a new character
        </button>
        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={onDifferentScenario}
            style={{ flex: 1, padding: '12px 0', borderRadius: 12, background: 'transparent', border: `1px solid ${ghostBorder}`, color: ghostColor, fontWeight: 700, fontSize: 12, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5, transition: 'border-color 0.15s, color 0.15s' }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = accent; e.currentTarget.style.color = accent; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = ghostBorder; e.currentTarget.style.color = ghostColor; }}>
            Different scenario <ChevronRight size={12} />
          </button>
          <button onClick={onExit}
            style={{ flex: 1, padding: '12px 0', borderRadius: 12, background: 'transparent', border: `1px solid ${ghostBorder}`, color: ghostColor, fontWeight: 700, fontSize: 12, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5, transition: 'border-color 0.15s, color 0.15s' }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = isDark ? 'rgba(255,255,255,0.22)' : 'rgba(0,0,0,0.2)'; e.currentTarget.style.color = isDark ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.7)'; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = ghostBorder; e.currentTarget.style.color = ghostColor; }}>
            Back to Skills
          </button>
        </div>
      </div>
    </div>
  );
};

/* ─── Auth gate ───
   The server decides who is in the beta (GET /voice/beta-status, the same list
   it enforces on every voice endpoint). VITE_BETA_EMAILS is DEPRECATED: it is
   read only as a fallback if that request cannot be answered, and will be
   removed in the next release. */
const BETA_EMAILS = (import.meta.env.VITE_BETA_EMAILS ?? 'plethora1000@gmail.com')
  .split(',')
  .map(e => e.trim().toLowerCase())
  .filter(Boolean);

const isAllowedByClientList = (email) => BETA_EMAILS.includes(email?.toLowerCase());

/* ═══════════════════════════════════════════════════════════
   MAIN VOICEROOM
═══════════════════════════════════════════════════════════ */
const VoiceRoom = () => {
  const { scenarioId }                 = useParams();
  const { user, loading: authLoading } = useAuth();
  const { recordActivity }             = useStreak();
  const navigate                       = useNavigate();
  const { play, stop, isPlaying }      = useAudioPlayer();

  /* ── Dark / mobile ── */
  const isDark = useDarkMode();
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' && window.innerWidth < 640
  );
  useEffect(() => {
    const r = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener('resize', r);
    return () => window.removeEventListener('resize', r);
  }, []);

  /* ── Character state ── */
  const [character, setCharacter]       = useState(null);
  const [charLoading, setCharLoading]   = useState(true);
  const [charError, setCharError]       = useState(null);
  // Set when a mid-call request fails (refused or unreachable) and we fall back
  // to scripted replies, so the user is told rather than left guessing.
  const [apiNotice, setApiNotice]       = useState(null);
  // null = not known yet. The server's answer wins; the deprecated client list is
  // used only if the server could not be asked.
  const [betaAllowed, setBetaAllowed]   = useState(null);

  useEffect(() => {
    if (authLoading) return undefined;
    if (!user) { setBetaAllowed(false); return undefined; }
    let cancelled = false;
    voiceApi('/voice/beta-status')
      .then((data) => { if (!cancelled) setBetaAllowed(data?.allowed === true); })
      .catch((err) => {
        console.warn('[VoiceRoom] beta-status failed, using the deprecated client list:', err.message);
        if (!cancelled) setBetaAllowed(isAllowedByClientList(user.email));
      });
    return () => { cancelled = true; };
  }, [authLoading, user]);

  /* ── Fetch random character on mount ── */
  useEffect(() => {
    let cancelled = false;

    const fetchCharacter = async () => {
      setCharLoading(true);
      setCharError(null);
      try {
        // If a specific character ID was passed via URL params, use it directly
        if (scenarioId && ALL_CHARACTERS[scenarioId]) {
          if (!cancelled) setCharacter(ALL_CHARACTERS[scenarioId]);
          return;
        }

        // No character in the URL (e.g. the Dashboard link): there is no current scenario to stay in,
        // so this is a random pick across all of them. "New character" later stays inside the scenario.
        const { id } = await voiceApi(nextCharacterPath(null));

        const found = ALL_CHARACTERS[id] ?? ALL_CHARACTERS[FALLBACK_ID];
        if (!cancelled) setCharacter(found);
      } catch (err) {
        console.error('[VoiceRoom] character fetch failed:', err.message);
        // Fall back to Henry Davies. A server-side refusal (401/403/429) says
        // why; anything else is a connectivity problem.
        if (!cancelled) {
          setCharacter(ALL_CHARACTERS[FALLBACK_ID]);
          setCharError(err.status
            ? `${err.message} Using a default character.`
            : 'Could not reach the server — using a default character.');
        }
      } finally {
        if (!cancelled) setCharLoading(false);
      }
    };

    fetchCharacter();
    return () => { cancelled = true; };
  }, [scenarioId]);

  const accent = character?.color ?? '#ea580c';
  usePageTitle(`${character?.name ?? 'Voice Room'} — Voice Room`);

  /* ── Conversation state ── */
  const [phase, setPhase]           = useState('briefing');
  const [transcript, setTranscript] = useState([]);
  const [prospectSpeaking, setProspect] = useState(false);
  const [userSpeaking, setUserSpeak]    = useState(false);
  const [turnCount, setTurnCount]   = useState(0);
  const [coaching, setCoaching]     = useState(null);
  const [coachScore, setCoachScore] = useState(null);
  const [coachSummary, setCoachSummary] = useState(null);
  const [timer, setTimer]           = useState(0);
  const [hungUp, setHungUp]         = useState(false);
  const [impatience, setImpatience] = useState(0);
  // True from the moment the rep finishes speaking until the prospect's reply (AI or
  // scripted) is about to be spoken. The silence/hang-up timer is paused meanwhile:
  // a slow /voice/respond is the app's wait, not the rep's silence.
  const [awaitingReply, setAwaitingReply] = useState(false);
  // True once any prospect reply in this call came from the scripted fallback. A ref
  // (read when the call ends, possibly from a stale closure) plus state (for the results line).
  const fallbackUsedRef = useRef(false);
  const [fallbackUsed, setFallbackUsed] = useState(false);

  // The transcript as DATA, updated synchronously with every change (the `transcript` state
  // above only drives rendering). endConversation is queued on a timer, so it would otherwise
  // read the `transcript` its render captured and send /voice/score without the last exchange.
  const transcriptLog  = useRef([]);
  // The silence stage most recently spoken since the rep last spoke (-1: none); see silence.js.
  const lastSpokenStageRef = useRef(-1);
  const [progressSaved, setProgressSaved] = useState(null);   // from /voice/score: true | false | null (unknown)

  const transcriptRef  = useRef(null);
  const timerRef       = useRef(null);
  const timeoutsRef    = useRef([]);
  const typingRef      = useRef(null);
  const lineId         = useRef(0);
  const ended          = useRef(false);
  const impatientRef   = useRef(null);
  const silenceRef     = useRef(null);
  // The server's signed stamp of when this call started: handed out by the first /voice/respond,
  // echoed on later ones, and sent back with /voice/score so the SERVER works out the duration.
  // (The browser used to time the call itself and send durationSeconds, which anyone could edit.)
  const sessionTokenRef = useRef(null);
  // Which call this is. Bumped whenever a call starts or the room is reset, so a reply that was
  // still on its way when the previous call ended cannot be spoken into the next one.
  const callIdRef = useRef(0);

  /* Stops everything a call has queued: the delayed hand-offs and end-of-call timers (push), the
     typing interval and the silence check. Without this, ending a call and starting another
     within a couple of seconds let the OLD call's timers (the 5-turn-cap end timer fires 2.5s
     after the last reply) run in the new one and end it. */
  const clearQueuedTimers = useCallback(() => {
    timeoutsRef.current.forEach(clearTimeout);
    timeoutsRef.current = [];
    if (typingRef.current)    { clearInterval(typingRef.current); typingRef.current = null; }
    if (impatientRef.current) { clearTimeout(impatientRef.current); impatientRef.current = null; }
  }, []);

  /* ── Cleanup (leaving the page) ── */
  useEffect(() => {
    return () => {
      clearQueuedTimers();
      if (timerRef.current) clearInterval(timerRef.current);
      stop();
    };
  }, [stop, clearQueuedTimers]);

  /* ── Auto-scroll ── */
  useEffect(() => {
    if (transcriptRef.current) transcriptRef.current.scrollTop = transcriptRef.current.scrollHeight;
  }, [transcript]);

  /* ── Session timer ── */
  useEffect(() => {
    if (phase === 'active') timerRef.current = setInterval(() => setTimer(t => t + 1), 1000);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [phase]);

  const fmt  = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
  const push = useCallback((cb, ms) => {
    const id = setTimeout(cb, ms);
    timeoutsRef.current.push(id);
    return id;
  }, []);

  /* Every change to the transcript goes through here, so the ref and the state never differ. */
  const updateTranscript = useCallback((fn) => {
    transcriptLog.current = fn(transcriptLog.current);
    setTranscript(transcriptLog.current);
  }, []);

  /* ── Prospect speaks ── */
  /* `slot` names the line being spoken ('opening' or a silence-stage lineKey).
     audioUrl() is null when no clip exists for it (or the line was edited after
     the clip was made), and then the line is text-only, with no request made.
     A live reply from /voice/respond has no slot; when the server spoke it, it comes with `liveAudio` (its
     audioUrl) and plays exactly like a pre-made clip: same player, same speaking indicator, text typed out
     after. If that audio fails to load or start, play() gives up and the text is shown as before. */
  const prospectSay = useCallback(async (text, slot = null, { scripted = false, liveAudio = null } = {}) => {
    if (ended.current || !character) return;
    setProspect(true); setUserSpeak(false);
    const clip = liveAudio ?? (slot ? audioUrl(character, slot) : null);
    if (clip) await play(clip, liveAudio ? { startTimeoutMs: LIVE_AUDIO_START_MS } : undefined);
    const words = text.split(' ');
    let built = '', i = 0;
    const id = lineId.current++;
    updateTranscript(prev => [...prev, { speaker: character.name, text: '', id, isUser: false, ...(scripted ? { scripted: true } : {}) }]);
    if (typingRef.current) clearInterval(typingRef.current);
    typingRef.current = setInterval(() => {
      built += (i > 0 ? ' ' : '') + words[i++];
      updateTranscript(prev => {
        const u = [...prev];
        u[u.length - 1] = { ...u[u.length - 1], text: built };
        return u;
      });
      if (i >= words.length) {
        clearInterval(typingRef.current);
        typingRef.current = null;
        setProspect(false);
        silenceRef.current = Date.now();
      }
    }, 55);
  }, [character, play, updateTranscript]);

  /* ── End conversation ── */
  const endConversation = useCallback(async (wasHungUp = false) => {
    if (ended.current || phase !== 'active') return;
    ended.current = true;
    setPhase('ended'); setHungUp(wasHungUp);
    if (timerRef.current)     clearInterval(timerRef.current);
    if (impatientRef.current) clearTimeout(impatientRef.current);
    stop();
    setProgressSaved(null);

    if (wasHungUp) {
      setCoaching([
        { type: 'improve', text: 'You let too much silence build up. Respond within 3–5 seconds — even a partial response beats dead air.' },
        { type: 'improve', text: `${character.name} hung up because the silence felt like you weren't there.` },
        { type: 'note',    text: "Filler phrases like \"That's a fair point\" buy you 5 seconds without losing momentum." },
      ]);
      setCoachScore(null);
      setCoachSummary(null);
      return;
    }

    /* ── Call DeepSeek scoring ── */
    try {
      const data = await voiceApi('/voice/score', {
        method: 'POST',
        body: {
          characterId: character.id,
          // the ref, not the `transcript` this closure captured: this runs from a timer that was
          // queued before the last exchange was added
          conversationHistory: toWireTranscript(transcriptLog.current),
          ...(sessionTokenRef.current ? { sessionToken: sessionTokenRef.current } : {}),
          // tells the coach some prospect lines were scripted (they are also marked per turn)
          ...(fallbackUsedRef.current ? { fallbackUsed: true } : {}),
        },
      });
      setCoaching(data.coaching ?? []);
      setCoachScore(data.score ?? null);
      setCoachSummary(data.summary ?? null);
      setProgressSaved(typeof data.progressSaved === 'boolean' ? data.progressSaved : null);
      // The streak counts a call only once it has been scored AND saved to progress.
      if (data.progressSaved === true) recordActivity().catch(console.error);
    } catch (err) {
      console.error('[VoiceRoom] scoring failed:', err.message);
      // err.status is set when the server refused (expired session, beta,
      // rate limit); its message says why. Otherwise it is a network problem.
      setCoaching([{
        type: 'note',
        text: err.status ? err.message : 'AI coaching unavailable this session. Try again.',
      }]);
      setCoachScore(null);
      setCoachSummary(null);
    }
  }, [phase, character, recordActivity, stop]);

  /* ── Start conversation ── */
  const startConversation = useCallback(() => {
    if (!character) return;
    clearQueuedTimers(); callIdRef.current += 1;   // nothing left over from a previous call may run in this one
    setPhase('active'); updateTranscript(() => []); setTurnCount(0);
    setTimer(0); setHungUp(false); setImpatience(0); lastSpokenStageRef.current = -1;
    setCoaching(null); setCoachScore(null); setCoachSummary(null); setProgressSaved(null);
    fallbackUsedRef.current = false; setFallbackUsed(false); setAwaitingReply(false);
    ended.current = false; silenceRef.current = Date.now();
    sessionTokenRef.current = null; setApiNotice(null);
    // openingLine() reads opening_a / opening / opening_b. Reading only
    // opening_a left the 115 characters that name it `opening` starting the
    // call with an empty line.
    push(() => prospectSay(openingLine(character), 'opening'), 400);
  }, [character, prospectSay, push, updateTranscript, clearQueuedTimers]);

  /* ── STT hook ── */
  const { start: startSTT, stop: stopSTT, isListening, isSupported: sttSupported, interim } = useSpeechRecognition({
    onResult: (text) => {
      if (phase !== 'active' || prospectSpeaking || ended.current) return;
      handleUserSpoke(text);
    },
    onStart: () => {
      setUserSpeak(true);
      setImpatience(0); lastSpokenStageRef.current = -1;
      if (impatientRef.current) clearTimeout(impatientRef.current);
      silenceRef.current = null;
    },
    onEnd: () => {
      setUserSpeak(false);
      silenceRef.current = Date.now();
    },
  });

  /* ── Process user speech — calls DeepSeek for prospect reply ── */
  const handleUserSpoke = useCallback(async (spokenText) => {
    if (phase !== 'active' || ended.current || !character) return;
    const callId = callIdRef.current;   // if a new call starts while we wait, this reply is for the old one: drop it

    const id = lineId.current++;
    updateTranscript(prev => [...prev, { speaker: 'you', text: spokenText, id, isUser: true }]);
    setUserSpeak(false);

    let prospectReply;
    let replyAudio = null;      // the server's spoken version of the reply, when it made one
    let usedFallback = false;
    setAwaitingReply(true);   // pauses the silence timer until the reply is ready to be spoken
    try {
      /* ── DeepSeek prospect response ── */
      const data = await voiceApi('/voice/respond', {
        method: 'POST',
        body: {
          characterId: character.id,          // the server looks the character up itself
          conversationHistory: toWireTranscript(transcript),
          userMessage: spokenText.trim().slice(0, WIRE_MAX_TURN_CHARS),
          ...(sessionTokenRef.current ? { sessionToken: sessionTokenRef.current } : {}),
        },
      });
      if (callId !== callIdRef.current) return;   // a different call is running now: do not touch its token, notice or transcript
      if (typeof data.text !== 'string' || !data.text.trim()) throw new Error('Empty reply from the server.');
      if (typeof data.sessionToken === 'string') sessionTokenRef.current = data.sessionToken;   // the first reply starts the call's clock
      prospectReply = data.text;
      replyAudio = liveReplyUrl(data.audioUrl);
      setApiNotice(null);
    } catch (err) {
      /* ── Scripted fallback if the API is unreachable, refused us, or sent nothing usable ── */
      if (callId !== callIdRef.current) return;   // the call this was for is over
      console.error('[VoiceRoom] respond failed:', err.message);
      setApiNotice(err.status
        ? `${err.message} Using scripted replies for now.`
        : 'Could not reach the server. Using scripted replies for now.');
      const fallbackKey = character.category ?? 'decision-maker-hook';
      const turns = SCRIPTED_TURNS[fallbackKey] ?? SCRIPTED_TURNS['decision-maker-hook'];
      prospectReply = turns.prospect[turnCount % turns.prospect.length].text;
      usedFallback = true;
      fallbackUsedRef.current = true;
      setFallbackUsed(true);
    }

    const isLastTurn = turnCount >= 4;
    setTurnCount(prev => prev + 1);

    push(() => {
      // prospectSay sets prospectSpeaking in the same tick, so there is no gap in
      // which the silence timer could run between the reply arriving and being spoken.
      setAwaitingReply(false);
      prospectSay(prospectReply, null, { scripted: usedFallback, liveAudio: replyAudio });
      if (isLastTurn) push(() => endConversation(false), 2500);
    }, 400);
  }, [phase, transcript, turnCount, character, push, prospectSay, endConversation, updateTranscript]);

  /* ── Mic button ── */
  const handleMicPress = useCallback(() => {
    if (phase !== 'active' || prospectSpeaking || isPlaying || ended.current) return;
    if (isListening) {
      stopSTT();
    } else {
      if (!sttSupported) {
        /* Scripted fallback for Firefox / unsupported browsers */
        const fallbackKey = character?.category ?? 'decision-maker-hook';
        const turns = SCRIPTED_TURNS[fallbackKey] ?? SCRIPTED_TURNS['decision-maker-hook'];
        handleUserSpoke(turns.user[turnCount % turns.user.length]);
        return;
      }
      startSTT();
    }
  }, [phase, prospectSpeaking, isPlaying, isListening, stopSTT, startSTT, sttSupported, character, turnCount, handleUserSpoke]);

  /* ── Silence / impatience detection (reads silenceProfile from character) ── */
  useEffect(() => {
    // Paused (clock cleared) while the prospect is speaking/typing, the rep is
    // speaking, or the app is still waiting for the prospect's reply.
    if (phase !== 'active' || prospectSpeaking || userSpeaking || isListening || awaitingReply || ended.current || !character) {
      silenceRef.current = null;
      return;
    }
    if (!silenceRef.current) silenceRef.current = Date.now();

    const stages = character.silenceProfile?.stages ?? DEFAULT_STAGES;

    const check = () => {
      if (ended.current || phase !== 'active') return;
      const silentMs = Date.now() - silenceRef.current;
      const currentStage = stages[impatience];
      if (!currentStage) return;

      // Stage delays are TOTAL silence (8s, 15s, 22s), but the clock restarts whenever the
      // prospect finishes a line, so a later stage waits only the difference. See silence.js.
      if (silentMs >= stageWaitMs(stages, impatience, lastSpokenStageRef.current)) {
        setImpatience(prev => prev + 1);
        const lineText = character.lines[currentStage.lineKey];
        if (lineText) {
          lastSpokenStageRef.current = impatience;   // this line restarts the clock
          prospectSay(lineText, currentStage.lineKey);
        }

        if (currentStage.type === 'hangup') {
          setHungUp(true);
          push(() => endConversation(true), 1500);
          return;
        }
      }

      if (!ended.current) impatientRef.current = setTimeout(check, 500);
    };

    impatientRef.current = setTimeout(check, 500);
    return () => { if (impatientRef.current) clearTimeout(impatientRef.current); };
  }, [phase, prospectSpeaking, userSpeaking, isListening, awaitingReply, impatience, character, prospectSay, endConversation, push]);

  /* ── Back to the briefing with nothing left over from the previous call ── */
  const resetCall = useCallback(() => {
    clearQueuedTimers(); callIdRef.current += 1;
    ended.current = false;
    setPhase('briefing'); updateTranscript(() => []); setCoaching(null);
    setCoachScore(null); setCoachSummary(null); setProgressSaved(null);
    setTurnCount(0); setTimer(0); setUserSpeak(false); setProspect(false);
    setHungUp(false); setImpatience(0); lastSpokenStageRef.current = -1; silenceRef.current = null;
    fallbackUsedRef.current = false; setFallbackUsed(false); setAwaitingReply(false);
    stop();

    setCharError(null); setApiNotice(null); sessionTokenRef.current = null;
  }, [stop, updateTranscript, clearQueuedTimers]);

  /* ── Restart — fetches a new random character ── */
  const restart = useCallback(async () => {
    resetCall();

    /* Pick a new random character from the SAME scenario, avoiding the current one */
    try {
      const { id } = await voiceApi(nextCharacterPath(character?.id));
      const found = ALL_CHARACTERS[id] ?? ALL_CHARACTERS[FALLBACK_ID];
      setCharacter(found);
    } catch (err) {
      /* Keep the current character, but say why instead of silently doing nothing */
      console.error('[VoiceRoom] next character failed:', err.message);
      setCharError(err.status ? err.message : 'Could not reach the server to pick a new character.');
    }
  }, [character, resetCall]);

  // Only the dev panel calls this; in a production build it is unused and removed.
  const devSelectCharacter = (id) => {
    if (!ALL_CHARACTERS[id]) return;
    resetCall();
    setCharacter(ALL_CHARACTERS[id]);
  };

  const handleDifferentScenario = useCallback(() => {
    clearQueuedTimers();
    navigate('/skills/sales-negotiation');
  }, [navigate, clearQueuedTimers]);

  const handleExit = useCallback(() => {
    clearQueuedTimers();
    navigate('/skills/sales-negotiation');
  }, [navigate, clearQueuedTimers]);

  /* ── Early returns ── */
  if (authLoading || charLoading || betaAllowed === null) return (
    <div style={{ position: 'fixed', inset: 0, background: isDark ? '#09090b' : '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 12 }}>
      <div style={{ width: 32, height: 32, border: `3px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`, borderTopColor: '#ea580c', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />
      <span style={{ fontSize: 14, color: isDark ? 'rgba(255,255,255,0.35)' : 'rgba(0,0,0,0.3)' }}>Loading your character...</span>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );

  if (!character) return (
    <div style={{ position: 'fixed', inset: 0, background: isDark ? '#09090b' : '#f5f5f7', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div style={{ textAlign: 'center', maxWidth: 360 }}>
        <div style={{ fontSize: 44, marginBottom: 16 }}>⚠️</div>
        <h1 style={{ fontSize: 22, fontWeight: 900, color: isDark ? '#fff' : '#111', marginBottom: 8 }}>Character not found</h1>
        <p style={{ fontSize: 14, color: isDark ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)', lineHeight: 1.6, marginBottom: 24 }}>
          Could not load a character. Check your connection and try again.
        </p>
        <button onClick={() => window.location.reload()}
          style={{ padding: '12px 28px', borderRadius: 10, border: 'none', background: '#ea580c', color: '#fff', fontWeight: 700, fontSize: 14, cursor: 'pointer', marginRight: 10 }}>
          Retry
        </button>
        <button onClick={() => navigate('/skills/sales-negotiation')}
          style={{ padding: '12px 28px', borderRadius: 10, border: 'none', background: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)', color: isDark ? '#fff' : '#111', fontWeight: 700, fontSize: 14, cursor: 'pointer' }}>
          Back to Skills
        </button>
      </div>
    </div>
  );

  /* A scenario that is not open yet ("coming soon") cannot be played, even from a direct link: nothing starts here, and the
     backend refuses the calls as well. The character and its data stay in the code. */
  const characterScenario = getScenarioOf(character.id);
  if (!isScenarioLive(characterScenario)) return (
    <div style={{ position: 'fixed', inset: 0, background: isDark ? '#09090b' : '#f5f5f7', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div style={{ textAlign: 'center', maxWidth: 380 }}>
        <div style={{ fontSize: 44, marginBottom: 16 }}>⏳</div>
        <h1 data-testid="scenario-coming-soon" style={{ fontSize: 26, fontWeight: 900, color: isDark ? '#fff' : '#111', marginBottom: 8 }}>Coming soon</h1>
        <p style={{ fontSize: 14, color: isDark ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)', lineHeight: 1.6, marginBottom: 24 }}>
          {SCENARIO_TITLES[characterScenario] ?? 'This scenario'} is not open yet. {character.name} and the other characters in it will be available when it launches.
        </p>
        <button onClick={() => navigate('/skills/sales-negotiation')}
          style={{ padding: '12px 28px', borderRadius: 10, border: 'none', background: accent, color: '#fff', fontWeight: 700, fontSize: 14, cursor: 'pointer' }}>
          Back to Sales &amp; Negotiation
        </button>
      </div>
      {import.meta.env.DEV && DevCharacterPanel && (
        <Suspense fallback={null}>
          <DevCharacterPanel character={character} onSelect={devSelectCharacter} />
        </Suspense>
      )}
    </div>
  );

  if (!betaAllowed) return (
    <div style={{ position: 'fixed', inset: 0, background: isDark ? '#09090b' : '#f5f5f7', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div style={{ textAlign: 'center', maxWidth: 360 }}>
        <div style={{ fontSize: 44, marginBottom: 16 }}>🎤</div>
        <h1 style={{ fontSize: 26, fontWeight: 900, color: isDark ? '#fff' : '#111', marginBottom: 8 }}>Coming Soon</h1>
        <p style={{ fontSize: 14, color: isDark ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)', lineHeight: 1.6, marginBottom: 24 }}>
          The Voice Room is currently in private beta.
        </p>
        <button onClick={() => navigate('/skills/sales-negotiation')}
          style={{ padding: '12px 28px', borderRadius: 10, border: 'none', background: accent, color: '#fff', fontWeight: 700, fontSize: 14, cursor: 'pointer' }}>
          Back to Skills
        </button>
      </div>
    </div>
  );

  /* ── Derived UI ── */
  const bg      = isDark ? '#09090b' : '#ffffff';
  const txt     = isDark ? 'rgba(255,255,255,0.92)' : '#111827';
  const muted   = isDark ? 'rgba(255,255,255,0.38)' : 'rgba(0,0,0,0.42)';
  const faint   = isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.06)';
  const barGrad = isDark
    ? 'linear-gradient(to top,#09090b 55%,transparent)'
    : 'linear-gradient(to top,#ffffff 55%,transparent)';
  const avatarBriefing = isMobile ? 120 : 160;
  const avatarStrip    = isMobile ? 52  : 68;

  return (
    <div style={{ position: 'fixed', inset: 0, background: bg, display: 'flex', flexDirection: 'column', fontFamily: 'system-ui,-apple-system,sans-serif', overflow: 'hidden' }}>
      <style>{`
        @keyframes fade-up   { from{opacity:0;transform:translateY(8px);}to{opacity:1;transform:translateY(0);} }
        @keyframes bubble-in { from{opacity:0;transform:scale(0.93) translateY(8px);}to{opacity:1;transform:scale(1) translateY(0);} }
        @keyframes avatar-float { 0%,100%{transform:translateY(0);}50%{transform:translateY(-6px);} }
        @keyframes ring-pulse { 0%,100%{box-shadow:0 0 0 0 ${accent}55;}50%{box-shadow:0 0 0 8px ${accent}00;} }
        @keyframes ripple-out { 0%{transform:scale(1);opacity:0.6;}100%{transform:scale(1.55);opacity:0;} }
        @keyframes dot-pulse  { 0%,100%{opacity:1;}50%{opacity:0.35;} }
        @keyframes interim-in { from{opacity:0;transform:translateY(4px);}to{opacity:1;transform:translateY(0);} }
        @keyframes spin       { to{transform:rotate(360deg);} }
        .mic-btn { transition:transform 0.12s ease; }
        .mic-btn:hover:not(:disabled) { transform:scale(1.06); }
        .mic-btn:active:not(:disabled) { transform:scale(0.93); }
      `}</style>

      {/* Background glow */}
      <div style={{ position: 'absolute', top: -200, left: '50%', transform: 'translateX(-50%)', width: 700, height: 500, pointerEvents: 'none', zIndex: 0, background: `radial-gradient(ellipse at center,${accent}07 0%,transparent 70%)` }} />

      {/* ── TOP BAR ── */}
      <div style={{ position: 'relative', zIndex: 10, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: `12px ${isMobile ? '16px' : '28px'}`, borderBottom: `1px solid ${faint}` }}>
        {(phase === 'briefing' || phase === 'ended') ? (
          <button onClick={handleExit}
            style={{ display: 'flex', alignItems: 'center', gap: 5, background: 'none', border: 'none', color: muted, cursor: 'pointer', fontSize: 13, fontWeight: 500, padding: 0, transition: 'color 0.15s' }}
            onMouseEnter={e => e.currentTarget.style.color = isDark ? 'rgba(255,255,255,0.65)' : 'rgba(0,0,0,0.65)'}
            onMouseLeave={e => e.currentTarget.style.color = muted}>
            <ArrowLeft size={15} /> Back
          </button>
        ) : <div style={{ width: 60 }} />}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {(phase === 'active' || phase === 'ended') && (
            <span style={{ fontSize: 13, fontWeight: 600, color: muted, fontVariantNumeric: 'tabular-nums' }}>{fmt(timer)}</span>
          )}
        </div>
        <div style={{ width: 60 }} />
      </div>

      {/* ── CHARACTER STRIP (active only) ── */}
      {phase === 'active' && (
        <div style={{ position: 'relative', zIndex: 10, flexShrink: 0, display: 'flex', alignItems: 'center', gap: 12, padding: `10px ${isMobile ? '16px' : '28px'}`, borderBottom: `1px solid ${faint}` }}>
          <div style={{ width: avatarStrip, height: avatarStrip, borderRadius: '50%', flexShrink: 0, background: `${accent}18`, overflow: 'hidden', border: `2.5px solid ${prospectSpeaking ? accent : accent + '30'}`, animation: prospectSpeaking ? 'ring-pulse 1.2s ease-in-out infinite' : 'none', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <CharacterPortrait character={character} size={avatarStrip} accent={accent} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 1, flexWrap: 'wrap' }}>
              <p style={{ fontSize: 14, fontWeight: 700, color: txt, margin: 0 }}>{character.name}</p>
              <div style={{ display: 'flex', alignItems: 'center', gap: 3, background: 'rgba(34,197,94,0.10)', border: '1px solid rgba(34,197,94,0.22)', borderRadius: 99, padding: '2px 8px' }}>
                <div style={{ width: 5, height: 5, borderRadius: '50%', background: '#22c55e', animation: 'dot-pulse 1.5s ease-in-out infinite' }} />
                <span style={{ fontSize: 9, fontWeight: 700, color: '#22c55e', letterSpacing: '0.06em' }}>LIVE</span>
              </div>
            </div>
            <p style={{ fontSize: 11, color: muted, margin: 0 }}>{character.role} · {character.company}</p>
          </div>
          <div style={{ fontSize: 9, fontWeight: 700, color: accent, background: `${accent}10`, border: `1px solid ${accent}20`, padding: '3px 9px', borderRadius: 99, letterSpacing: '0.06em', textTransform: 'uppercase', flexShrink: 0 }}>
            {character.scenarioTitle}
          </div>
        </div>
      )}

      {/* ── API NOTICE (a mid-call request failed; scripted fallback in use) ── */}
      {apiNotice && (phase === 'active' || phase === 'ended') && (
        <div role="status" style={{ position: 'relative', zIndex: 10, flexShrink: 0, background: 'rgba(245,158,11,0.08)', borderBottom: '1px solid rgba(245,158,11,0.2)', padding: `8px ${isMobile ? '16px' : '28px'}` }}>
          <p style={{ fontSize: 12, color: '#f59e0b', margin: 0 }}>⚠️ {apiNotice}</p>
        </div>
      )}

      {/* ── MAIN CONTENT ── */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', position: 'relative', overflow: 'hidden', zIndex: 5 }}>

        {/* BRIEFING */}
        {phase === 'briefing' && (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 20, maxWidth: 520, width: '100%', margin: '0 auto', padding: `0 ${isMobile ? '20px' : '28px'}`, animation: 'fade-up 0.4s ease-out both' }}>
            {charError && (
              <div style={{ background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.2)', borderRadius: 10, padding: '8px 14px', width: '100%', textAlign: 'center' }}>
                <p style={{ fontSize: 12, color: '#f59e0b', margin: 0 }}>⚠️ {charError}</p>
              </div>
            )}
            <div style={{ width: avatarBriefing, height: avatarBriefing, borderRadius: '50%', background: `${accent}12`, border: `3px solid ${accent}28`, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', boxShadow: `0 12px 48px ${accent}20`, animation: 'avatar-float 4s ease-in-out infinite', flexShrink: 0 }}>
              <CharacterPortrait character={character} size={avatarBriefing} accent={accent} />
            </div>
            <div style={{ textAlign: 'center' }}>
              <p style={{ fontSize: 20, fontWeight: 800, color: txt, margin: 0 }}>{character.name}</p>
              <p style={{ fontSize: 13, color: muted, margin: '4px 0 8px' }}>{character.role} · {character.company}</p>
              <span style={{ display: 'inline-block', fontSize: 10, fontWeight: 700, padding: '3px 10px', borderRadius: 99, background: 'rgba(239,68,68,0.12)', color: '#ef4444', border: '1px solid rgba(239,68,68,0.2)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                {character.difficulty}
              </span>
            </div>
            <div style={{ textAlign: 'center', width: '100%' }}>
              <h2 style={{ fontSize: isMobile ? 22 : 26, fontWeight: 800, color: txt, letterSpacing: '-0.02em', margin: '0 0 10px', lineHeight: 1.2 }}>{character.scenarioTitle}</h2>
              <p style={{ fontSize: 14, color: muted, lineHeight: 1.65, margin: 0 }}>{character.scenarioDesc}</p>
            </div>
            <div style={{ background: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)', border: `1px solid ${isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.07)'}`, borderRadius: 16, padding: '16px 22px', width: '100%', textAlign: 'center' }}>
              <p style={{ fontSize: 11, color: muted, textTransform: 'uppercase', letterSpacing: '0.1em', margin: '0 0 7px', fontWeight: 700 }}>Your Goal</p>
              <p style={{ fontSize: 15, fontWeight: 700, color: accent, margin: 0 }}>{character.goal}</p>
            </div>
            {!sttSupported && (
              <div style={{ background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.2)', borderRadius: 12, padding: '10px 16px', width: '100%', textAlign: 'center' }}>
                <p style={{ fontSize: 12, color: '#f59e0b', margin: 0 }}>
                  ⚠️ Your browser doesn't support live speech. Tap the mic to use scripted responses instead.
                </p>
              </div>
            )}
            <button onClick={startConversation}
              style={{ ...glowBtnStyle(accent), padding: '16px 52px', borderRadius: 100, color: accent, fontWeight: 700, fontSize: 15, backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)' }}
              onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-1px)'}
              onMouseLeave={e => e.currentTarget.style.transform = 'translateY(0)'}>
              Start Conversation
            </button>
          </div>
        )}

        {/* ACTIVE */}
        {phase === 'active' && (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <div ref={transcriptRef} style={{ flex: 1, overflowY: 'auto', paddingTop: 16, paddingBottom: 200, scrollBehavior: 'smooth', scrollbarWidth: 'none' }}>
              {transcript.length === 0 && (
                <div style={{ textAlign: 'center', padding: '40px 0', color: muted }}>
                  <p style={{ fontSize: 13, fontStyle: 'italic', margin: 0 }}>Conversation starting...</p>
                </div>
              )}
              {transcript.map((line, i) => (
                <Bubble key={line.id} speaker={line.speaker} text={line.text} isUser={line.isUser} accent={accent} isDark={isDark} isNew={i === transcript.length - 1} />
              ))}
            </div>

            {/* Bottom bar */}
            <div style={{ position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 20, background: barGrad, padding: `0 ${isMobile ? '16px' : '28px'} ${isMobile ? '20px' : '28px'}`, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, pointerEvents: 'none' }}>
              {interim && (
                <div style={{ pointerEvents: 'none', background: isDark ? 'rgba(0,0,0,0.75)' : 'rgba(255,255,255,0.92)', backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)', border: '1px solid rgba(34,197,94,0.3)', borderRadius: 14, padding: '8px 16px', maxWidth: isMobile ? '85%' : '60%', animation: 'interim-in 0.2s ease-out both' }}>
                  <p style={{ fontSize: 13, color: isDark ? 'rgba(255,255,255,0.8)' : '#1f2937', margin: 0, fontStyle: 'italic', lineHeight: 1.4 }}>{interim}</p>
                </div>
              )}
              <div style={{ width: '100%', maxWidth: 580, pointerEvents: 'auto' }}>
                <Waveform userSpeaking={userSpeaking || isListening} prospectSpeaking={prospectSpeaking} />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 16 : 20, pointerEvents: 'auto' }}>
                <button onClick={() => endConversation(false)} title="End session"
                  style={{ width: 44, height: 44, borderRadius: '50%', border: '1px solid rgba(239,68,68,0.25)', background: 'rgba(239,68,68,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', transition: 'background 0.15s', flexShrink: 0 }}
                  onMouseEnter={e => e.currentTarget.style.background = 'rgba(239,68,68,0.2)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'rgba(239,68,68,0.08)'}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2.2">
                    <path d="M10.68 13.31a16 16 0 0 0 3.41 2.6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7 2 2 0 0 1 1.72 2v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07C9.44 16.29 7.62 14.48 6.29 12.37A19.79 19.79 0 0 1 3.22 3.74 2 2 0 0 1 5.19 1.55h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L9.17 9.47A16 16 0 0 0 10.68 13.31Z" />
                    <line x1="1" y1="1" x2="23" y2="23" />
                  </svg>
                </button>
                <div style={{ position: 'relative', flexShrink: 0 }}>
                  {(userSpeaking || isListening) && (
                    <div style={{ position: 'absolute', inset: -10, borderRadius: '50%', border: '2px solid #22c55e', animation: 'ripple-out 1.4s ease-out infinite', pointerEvents: 'none' }} />
                  )}
                  <button className="mic-btn" onClick={handleMicPress} disabled={prospectSpeaking || isPlaying}
                    style={{
                      width: isMobile ? 56 : 64, height: isMobile ? 56 : 64, borderRadius: '50%', border: 'none',
                      background: (userSpeaking || isListening) ? '#22c55e' : (isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.06)'),
                      outline: `2px solid ${(userSpeaking || isListening) ? 'transparent' : (isDark ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.08)')}`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      cursor: (prospectSpeaking || isPlaying) ? 'not-allowed' : 'pointer',
                      opacity: (prospectSpeaking || isPlaying) ? 0.38 : 1,
                      boxShadow: (userSpeaking || isListening) ? '0 0 40px rgba(34,197,94,0.35)' : 'none',
                    }}>
                    <Mic size={isMobile ? 20 : 24} color={(userSpeaking || isListening) ? '#fff' : (isDark ? 'rgba(255,255,255,0.45)' : 'rgba(0,0,0,0.4)')} />
                  </button>
                </div>
                <div style={{ width: 44 }} />
              </div>
              <p style={{ fontSize: 11, color: muted, letterSpacing: '0.05em', fontWeight: 500, pointerEvents: 'auto', margin: 0 }}>
                {isPlaying || prospectSpeaking
                  ? `${character.name} is speaking...`
                  : isListening
                  ? 'Listening... tap mic to send'
                  : sttSupported
                  ? 'Tap mic to respond'
                  : 'Tap mic to respond (scripted)'}
              </p>
            </div>
          </div>
        )}

        {/* ENDED */}
        {phase === 'ended' && coaching && (
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '32px 0', overflowY: 'auto' }}>
            <CoachingPanel
              feedback={coaching}
              score={coachScore}
              summary={coachSummary}
              onRestart={restart}
              onDifferentScenario={handleDifferentScenario}
              onExit={handleExit}
              accent={accent}
              isDark={isDark}
              hungUp={hungUp}
              fallbackUsed={fallbackUsed}
              progressSaved={progressSaved}
            />
          </div>
        )}
      </div>

      {import.meta.env.DEV && DevCharacterPanel && (
        <Suspense fallback={null}>
          <DevCharacterPanel character={character} onSelect={devSelectCharacter} />
        </Suspense>
      )}
    </div>
  );
};

export default VoiceRoom;